// ═══ BROKER RISK SCORE CONFIGURATION ═══
// 4 categories, 100 total. Group-max sums exactly match category caps.
// Revenue=30, Position=10, Compliance=35, Strategy=25 → Total=100

export const DEFAULT_RISK_CONFIG = {
  categories: {
    revenue:    { label: "Revenue Risk",    maxPts: 30, color: "#ef4444" },
    position:   { label: "Position Risk",   maxPts: 10, color: "#06b6d4" },
    compliance: { label: "Compliance Risk", maxPts: 35, color: "#f59e0b" },
    strategy:   { label: "Strategy Risk",   maxPts: 25, color: "#a855f7" },
  },
  levels: [
    { min: 0,  max: 15, label: "Safe",     color: "#22c55e" },
    { min: 16, max: 26, label: "Low",      color: "#06b6d4" },
    { min: 27, max: 38, label: "Elevated", color: "#f59e0b" },
    { min: 39, max: 50, label: "High",     color: "#f97316" },
    { min: 51, max: 100,label: "Critical", color: "#ef4444" },
  ],
  factors: {
    // ══════════════════════════════════════════════
    // REVENUE RISK (30 pts)
    // Group-max: 4+6+6+4+3+2+2+3 = 30 ✓
    // ══════════════════════════════════════════════
    revenue: [
      // GainerTrader (ungrouped) → 4 pts
      { id: "gainer",      label: "GainerTrader",           type: "flag", column: "GainerTrader",  flagValue: "GainerTrader", pts: 4 },
      // ProfitFactor group → max 6
      { id: "pf_2_5",      label: "ProfitFactor ≥ 2.5",     type: "value", column: "ProfitFactor", op: ">=", threshold: 2.5,   pts: 6 },
      { id: "pf_1_5",      label: "ProfitFactor ≥ 1.5",     type: "value", column: "ProfitFactor", op: ">=", threshold: 1.5,   pts: 4 },
      { id: "pf_1",        label: "ProfitFactor ≥ 1.0",     type: "value", column: "ProfitFactor", op: ">=", threshold: 1.0,   pts: 3 },
      { id: "pf_07",       label: "ProfitFactor ≥ 0.7",     type: "value", column: "ProfitFactor", op: ">=", threshold: 0.7,   pts: 1 },
      // Profit group → max 6
      { id: "profit_5k",   label: "Profit > $5K",           type: "value", column: "Profit",       op: ">",  threshold: 5000,  pts: 6 },
      { id: "profit_1k",   label: "Profit > $1K",           type: "value", column: "Profit",       op: ">",  threshold: 1000,  pts: 4 },
      { id: "profit_100",  label: "Profit > $100",          type: "value", column: "Profit",       op: ">",  threshold: 100,   pts: 2 },
      { id: "profit_0",    label: "Profit > $0",            type: "value", column: "Profit",       op: ">",  threshold: 0,     pts: 1 },
      // ProfitPct (Win Rate) group → max 4
      { id: "winrate_75",  label: "ProfitPct ≥ 75%",        type: "value", column: "ProfitPct",    op: ">=", threshold: 75,    pts: 4 },
      { id: "winrate_65",  label: "ProfitPct ≥ 65%",        type: "value", column: "ProfitPct",    op: ">=", threshold: 65,    pts: 2 },
      { id: "winrate_55",  label: "ProfitPct ≥ 55%",        type: "value", column: "ProfitPct",    op: ">=", threshold: 55,    pts: 1 },
      // BestTrade group → max 3
      { id: "best_5k",     label: "BestTrade > $5K",        type: "value", column: "BestTrade",    op: ">",  threshold: 5000,  pts: 3 },
      { id: "best_1k",     label: "BestTrade > $1K",        type: "value", column: "BestTrade",    op: ">",  threshold: 1000,  pts: 2 },
      { id: "best_100",    label: "BestTrade > $100",       type: "value", column: "BestTrade",    op: ">",  threshold: 100,   pts: 1 },
      // GrossProfit group → max 2
      { id: "gross_30k",   label: "GrossProfit > $30K",     type: "value", column: "GrossProfit",  op: ">",  threshold: 30000, pts: 2 },
      { id: "gross_5k",    label: "GrossProfit > $5K",      type: "value", column: "GrossProfit",  op: ">",  threshold: 5000,  pts: 1.5 },
      { id: "gross_1k",    label: "GrossProfit > $1K",      type: "value", column: "GrossProfit",  op: ">",  threshold: 1000,  pts: 1 },
      // AvgProfitTrade group → max 2
      { id: "avgprof_5k",  label: "AvgProfitTrade > $5K",   type: "value", column: "AvgProfitTrade", op: ">", threshold: 5000, pts: 2 },
      { id: "avgprof_1k",  label: "AvgProfitTrade > $1K",   type: "value", column: "AvgProfitTrade", op: ">", threshold: 1000, pts: 1.5 },
      { id: "avgprof_100", label: "AvgProfitTrade > $100",  type: "value", column: "AvgProfitTrade", op: ">", threshold: 100,  pts: 1 },
      // Payoff Ratio group → max 3
      { id: "payoff_5",    label: "Payoff Ratio > 5",       type: "computed", compute: "payoffRatio", op: ">", threshold: 5,   pts: 3 },
      { id: "payoff_3",    label: "Payoff Ratio > 3",       type: "computed", compute: "payoffRatio", op: ">", threshold: 3,   pts: 2 },
      { id: "payoff_2",    label: "Payoff Ratio > 2",       type: "computed", compute: "payoffRatio", op: ">", threshold: 2,   pts: 1 },
    ],
    revenue_groups: [
      { ids: ["pf_2_5", "pf_1_5", "pf_1", "pf_07"], label: "ProfitFactor" },
      { ids: ["profit_5k", "profit_1k", "profit_100", "profit_0"], label: "Profit" },
      { ids: ["winrate_75", "winrate_65", "winrate_55"], label: "ProfitPct" },
      { ids: ["best_5k", "best_1k", "best_100"], label: "BestTrade" },
      { ids: ["gross_30k", "gross_5k", "gross_1k"], label: "GrossProfit" },
      { ids: ["avgprof_5k", "avgprof_1k", "avgprof_100"], label: "AvgProfitTrade" },
      { ids: ["payoff_5", "payoff_3", "payoff_2"], label: "Payoff Ratio" },
    ],

    // ══════════════════════════════════════════════
    // POSITION RISK (10 pts)
    // Group-max: 2+2+2+2+1+1 = 10 ✓ (unchanged)
    // ══════════════════════════════════════════════
    position: [
      { id: "eqrisk_h",    label: "EquityRisk = High",      type: "flag", column: "EquityRisk", flagValue: "High",     pts: 2 },
      { id: "eqrisk_m",    label: "EquityRisk = Moderate",   type: "flag", column: "EquityRisk", flagValue: "Moderate", pts: 1 },
      { id: "eq_100k",     label: "Equity > $100K",          type: "value", column: "Equity", op: ">", threshold: 100000, pts: 2 },
      { id: "eq_20k",      label: "Equity > $20K",           type: "value", column: "Equity", op: ">", threshold: 20000,  pts: 1.5 },
      { id: "eq_5k",       label: "Equity > $5K",            type: "value", column: "Equity", op: ">", threshold: 5000,   pts: 1 },
      { id: "eqgrow_50",   label: "Equity Growth > 50%",     type: "computed", compute: "equityGrowth", op: ">", threshold: 50, pts: 2 },
      { id: "eqgrow_20",   label: "Equity Growth > 20%",     type: "computed", compute: "equityGrowth", op: ">", threshold: 20, pts: 1 },
      { id: "float_5k",    label: "FloatingPL > $5K",        type: "value", column: "FloatingPL", op: ">", threshold: 5000,  pts: 2 },
      { id: "float_1k",    label: "FloatingPL > $1K",        type: "value", column: "FloatingPL", op: ">", threshold: 1000,  pts: 1.5 },
      { id: "float_100",   label: "FloatingPL > $100",       type: "value", column: "FloatingPL", op: ">", threshold: 100,   pts: 1 },
      { id: "openpos_20",  label: "OpenPositions > 20",      type: "value", column: "StillOpenPositions", op: ">", threshold: 20, pts: 1 },
      { id: "openpos_5",   label: "OpenPositions > 5",       type: "value", column: "StillOpenPositions", op: ">", threshold: 5,  pts: 0.5 },
      { id: "openvol_5",   label: "OpenVolume > 5 lots",     type: "value", column: "StillOpenVolumeLots", op: ">", threshold: 5, pts: 1 },
      { id: "openvol_1",   label: "OpenVolume > 1 lot",      type: "value", column: "StillOpenVolumeLots", op: ">", threshold: 1, pts: 0.5 },
    ],
    position_groups: [
      { ids: ["eqrisk_h", "eqrisk_m"], label: "EquityRisk" },
      { ids: ["eq_100k", "eq_20k", "eq_5k"], label: "Equity" },
      { ids: ["eqgrow_50", "eqgrow_20"], label: "Equity Growth" },
      { ids: ["float_5k", "float_1k", "float_100"], label: "FloatingPL" },
      { ids: ["openpos_20", "openpos_5"], label: "OpenPositions" },
      { ids: ["openvol_5", "openvol_1"], label: "OpenVolume" },
    ],

    // ══════════════════════════════════════════════
    // COMPLIANCE RISK (35 pts)
    // Group-max: 4+3+2+3+3+3+3+3+3+2+3+3 = 35 ✓
    // ══════════════════════════════════════════════
    compliance: [
      // Speed <30s group (with Scalper) → max 4
      { id: "spd30_50",    label: "Speed <30s > 50%",       type: "value", column: "PctUnder30Sec", op: ">", threshold: 50, pts: 4 },
      { id: "spd30_30",    label: "Speed <30s > 30%",       type: "value", column: "PctUnder30Sec", op: ">", threshold: 30, pts: 3 },
      { id: "spd30_10",    label: "Speed <30s > 10%",       type: "value", column: "PctUnder30Sec", op: ">", threshold: 10, pts: 2 },
      { id: "scalper",     label: "Scalper",                type: "flag", column: "Scalper", flagValue: "Scalper", pts: 3 },
      // Speed <2min group → max 3
      { id: "spd2m_60",    label: "Speed <2min > 60%",      type: "value", column: "PctUnder2Min", op: ">", threshold: 60, pts: 3 },
      { id: "spd2m_40",    label: "Speed <2min > 40%",      type: "value", column: "PctUnder2Min", op: ">", threshold: 40, pts: 2 },
      { id: "spd2m_20",    label: "Speed <2min > 20%",      type: "value", column: "PctUnder2Min", op: ">", threshold: 20, pts: 1 },
      // Speed <5min group → max 2
      { id: "spd5m_80",    label: "Speed <5min > 80%",      type: "value", column: "PctUnder5Min", op: ">", threshold: 80, pts: 2 },
      { id: "spd5m_60",    label: "Speed <5min > 60%",      type: "value", column: "PctUnder5Min", op: ">", threshold: 60, pts: 1.5 },
      { id: "spd5m_40",    label: "Speed <5min > 40%",      type: "value", column: "PctUnder5Min", op: ">", threshold: 40, pts: 1 },
      // SpeedConsistency group → max 3
      { id: "spdcon_15",   label: "SpeedConsistency < 0.15",type: "value", column: "SpeedConsistency", op: "<", threshold: 0.15, pts: 3 },
      { id: "spdcon_25",   label: "SpeedConsistency < 0.25",type: "value", column: "SpeedConsistency", op: "<", threshold: 0.25, pts: 2 },
      { id: "spdcon_40",   label: "SpeedConsistency < 0.4", type: "value", column: "SpeedConsistency", op: "<", threshold: 0.4,  pts: 1 },
      // FastWinRate group → max 3
      { id: "fwr_85",      label: "FastWinRate > 85%",      type: "value", column: "FastTradeWinRate", op: ">", threshold: 85, pts: 3 },
      { id: "fwr_75",      label: "FastWinRate > 75%",      type: "value", column: "FastTradeWinRate", op: ">", threshold: 75, pts: 2 },
      { id: "fwr_65",      label: "FastWinRate > 65%",      type: "value", column: "FastTradeWinRate", op: ">", threshold: 65, pts: 1 },
      // ConsecutiveWins group → max 3 (reduced from 4)
      { id: "streak_25",   label: "ConsecWins ≥ 25",        type: "value", column: "MaxConsecutiveWins", op: ">=", threshold: 25, pts: 3 },
      { id: "streak_15",   label: "ConsecWins ≥ 15",        type: "value", column: "MaxConsecutiveWins", op: ">=", threshold: 15, pts: 2 },
      { id: "streak_10",   label: "ConsecWins ≥ 10",        type: "value", column: "MaxConsecutiveWins", op: ">=", threshold: 10, pts: 1.5 },
      // FridayLateOpens group → max 3
      { id: "fri_open_10", label: "FridayLateOpens > 10",   type: "value", column: "FridayLateOpens", op: ">", threshold: 10, pts: 3 },
      { id: "fri_open_5",  label: "FridayLateOpens > 5",    type: "value", column: "FridayLateOpens", op: ">", threshold: 5,  pts: 2 },
      { id: "fri_open_0",  label: "FridayLateOpens > 0",    type: "value", column: "FridayLateOpens", op: ">", threshold: 0,  pts: 1 },
      // FridayHedgeBreaks group (with HedgingAbuser) → max 3
      { id: "fri_hb_5",    label: "FridayHedgeBreaks > 5",  type: "value", column: "FridayHedgeBreaks", op: ">", threshold: 5, pts: 3 },
      { id: "fri_hb_3",    label: "FridayHedgeBreaks > 3",  type: "value", column: "FridayHedgeBreaks", op: ">", threshold: 3, pts: 2 },
      { id: "fri_hb_0",    label: "FridayHedgeBreaks > 0",  type: "value", column: "FridayHedgeBreaks", op: ">", threshold: 0, pts: 1 },
      { id: "hedgabuser",  label: "HedgingAbuser",          type: "flag", column: "HedgingAbuser", flagValue: "HedgingAbuser", pts: 2 },
      // Swap Abuse group (with Swaper) → max 3
      { id: "swap_10k",    label: "|SwapInOut| > $10K",     type: "computed", compute: "absSwapInOut", op: ">", threshold: 10000, pts: 3 },
      { id: "swap_5k",     label: "|SwapInOut| > $5K",      type: "computed", compute: "absSwapInOut", op: ">", threshold: 5000,  pts: 2 },
      { id: "swap_1k",     label: "|SwapInOut| > $1K",      type: "computed", compute: "absSwapInOut", op: ">", threshold: 1000,  pts: 1 },
      { id: "swaper",      label: "Swaper",                 type: "flag", column: "Swaper", flagValue: "Swaper", pts: 2 },
      // IP Abuse group (with IP_Abuser) → max 2 (reduced from 3)
      { id: "ipodt_10",    label: "IP_ODT > 10",            type: "value", column: "IP_ODT", op: ">", threshold: 10, pts: 2 },
      { id: "ipodt_5",     label: "IP_ODT > 5",             type: "value", column: "IP_ODT", op: ">", threshold: 5,  pts: 1.5 },
      { id: "ipodt_1",     label: "IP_ODT > 1",             type: "value", column: "IP_ODT", op: ">", threshold: 1,  pts: 1 },
      { id: "ip_abuser",   label: "IP_Abuser",              type: "flag", column: "IP_Abuser", flagValue: "IP_Abuser", pts: 1 },
      // Commission Abuse group (with Commissioner) → max 3
      { id: "comm_5k",     label: "Commission > $5K",       type: "value", column: "CommissionRebateExploitation", op: ">", threshold: 5000, pts: 3 },
      { id: "comm_1k",     label: "Commission > $1K",       type: "value", column: "CommissionRebateExploitation", op: ">", threshold: 1000, pts: 2 },
      { id: "comm_0",      label: "Commission > $0",        type: "value", column: "CommissionRebateExploitation", op: ">", threshold: 0,    pts: 1 },
      { id: "commissioner",label: "Commissioner",           type: "flag", column: "Commissioner", flagValue: "Commissioner", pts: 2 },
      // News Trading group (with NewsTrader) → max 3
      { id: "news_30",     label: "NewsTrades > 30",        type: "value", column: "TradesNearNews10Min", op: ">", threshold: 30, pts: 3 },
      { id: "news_20",     label: "NewsTrades > 20",        type: "value", column: "TradesNearNews10Min", op: ">", threshold: 20, pts: 2 },
      { id: "news_10",     label: "NewsTrades > 10",        type: "value", column: "TradesNearNews10Min", op: ">", threshold: 10, pts: 1 },
      { id: "newstrader",  label: "NewsTrader",             type: "flag", column: "NewsTrader", flagValue: "NewsTrader", pts: 1 },
    ],
    compliance_groups: [
      { ids: ["spd30_50", "spd30_30", "spd30_10", "scalper"], label: "Speed <30s" },
      { ids: ["spd2m_60", "spd2m_40", "spd2m_20"], label: "Speed <2min" },
      { ids: ["spd5m_80", "spd5m_60", "spd5m_40"], label: "Speed <5min" },
      { ids: ["spdcon_15", "spdcon_25", "spdcon_40"], label: "SpeedConsistency" },
      { ids: ["fwr_85", "fwr_75", "fwr_65"], label: "FastWinRate" },
      { ids: ["streak_25", "streak_15", "streak_10"], label: "ConsecutiveWins" },
      { ids: ["fri_open_10", "fri_open_5", "fri_open_0"], label: "FridayLateOpens" },
      { ids: ["fri_hb_5", "fri_hb_3", "fri_hb_0", "hedgabuser"], label: "FridayHedgeBreaks" },
      { ids: ["swap_10k", "swap_5k", "swap_1k", "swaper"], label: "Swap Abuse" },
      { ids: ["ipodt_10", "ipodt_5", "ipodt_1", "ip_abuser"], label: "IP Abuse" },
      { ids: ["comm_5k", "comm_1k", "comm_0", "commissioner"], label: "Commission Abuse" },
      { ids: ["news_30", "news_20", "news_10", "newstrader"], label: "News Trading" },
    ],

    // ══════════════════════════════════════════════
    // STRATEGY RISK (25 pts)
    // Group-max: 2+3+3+3+2+3+3+2+2+2 = 25 ✓
    // ct_hedger moved INTO HedgeRatio group
    // ══════════════════════════════════════════════
    strategy: [
      // Flags
      { id: "heavy",       label: "HeavyTrader",            type: "flag", column: "HeavyTrader",            flagValue: "HeavyTrader",            pts: 3 },
      { id: "hft",         label: "HighFrequencyTrader",     type: "flag", column: "HighFrequencyTrader",     flagValue: "HighFrequencyTrader",     pts: 3 },
      { id: "hedger",      label: "Hedger",                  type: "flag", column: "Hedger",                  flagValue: "Hedger",                  pts: 1 },
      { id: "longtrader",  label: "LongTrader",              type: "flag", column: "LongTrader",              flagValue: "LongTrader",              pts: 1 },
      // Loss/Profit time ratio group → max 2
      { id: "ratio_05",    label: "Loss/Profit Ratio < 0.5", type: "computed", compute: "lossProfitRatio", op: "<", threshold: 0.5, pts: 2 },
      { id: "ratio_1",     label: "Loss/Profit Ratio < 1.0", type: "computed", compute: "lossProfitRatio", op: "<", threshold: 1.0, pts: 1 },
      // DailyVolumeLots group (with Heavy) → max 3
      { id: "dvol_12",     label: "DailyVolume > 12 lots",   type: "value", column: "DailyVolumeLots", op: ">", threshold: 12,  pts: 3 },
      { id: "dvol_5",      label: "DailyVolume > 5 lots",    type: "value", column: "DailyVolumeLots", op: ">", threshold: 5,   pts: 2 },
      { id: "dvol_1",      label: "DailyVolume > 1 lot",     type: "value", column: "DailyVolumeLots", op: ">", threshold: 1,   pts: 1 },
      // TradingFreqPerDay group (with HFT) → max 3
      { id: "freq_80",     label: "TradingFreq > 80/day",    type: "value", column: "TradingFreqPerDay", op: ">", threshold: 80, pts: 3 },
      { id: "freq_50",     label: "TradingFreq > 50/day",    type: "value", column: "TradingFreqPerDay", op: ">", threshold: 50, pts: 2 },
      { id: "freq_25",     label: "TradingFreq > 25/day",    type: "value", column: "TradingFreqPerDay", op: ">", threshold: 25, pts: 1 },
      // MostUsedTradeSize group → max 3
      { id: "tsize_05",    label: "TradeSize > 0.5 lots",    type: "value", column: "MostUsedTradeSize", op: ">", threshold: 0.5,  pts: 3 },
      { id: "tsize_02",    label: "TradeSize > 0.2 lots",    type: "value", column: "MostUsedTradeSize", op: ">", threshold: 0.2,  pts: 2 },
      { id: "tsize_005",   label: "TradeSize > 0.05 lots",   type: "value", column: "MostUsedTradeSize", op: ">", threshold: 0.05, pts: 1 },
      // AvgTradeDuration group (with LongTrader) → max 2
      { id: "dur_300",     label: "AvgDuration < 5 min",     type: "value", column: "AvgTradeDuration", op: "<", threshold: 300,  pts: 2 },
      { id: "dur_3600",    label: "AvgDuration < 1 hour",    type: "value", column: "AvgTradeDuration", op: "<", threshold: 3600, pts: 1 },
      // Directional Bias group → max 3
      { id: "long_95",     label: "LongPct extreme > 95%",   type: "computed", compute: "longPctExtreme", op: ">", threshold: 95, pts: 3 },
      { id: "long_90",     label: "LongPct extreme > 90%",   type: "computed", compute: "longPctExtreme", op: ">", threshold: 90, pts: 2 },
      { id: "long_80",     label: "LongPct extreme > 80%",   type: "computed", compute: "longPctExtreme", op: ">", threshold: 80, pts: 1 },
      // HedgeRatio group (with Hedger + ct_hedger) → max 3
      { id: "hratio_07",   label: "HedgeRatio > 0.7",        type: "value", column: "HedgeRatio", op: ">", threshold: 0.7, pts: 3 },
      { id: "hratio_05",   label: "HedgeRatio > 0.5",        type: "value", column: "HedgeRatio", op: ">", threshold: 0.5, pts: 2 },
      { id: "hratio_03",   label: "HedgeRatio > 0.3",        type: "value", column: "HedgeRatio", op: ">", threshold: 0.3, pts: 1 },
      { id: "ct_hedger",   label: "ClientType = Hedger",     type: "flag", column: "ClientType", flagValue: "Hedger", pts: 2 },
      // HedgeVolume group → max 2
      { id: "hvol_20",     label: "HedgeVolume > 20 lots",   type: "value", column: "HedgeVolumeLots", op: ">", threshold: 20, pts: 2 },
      { id: "hvol_5",      label: "HedgeVolume > 5 lots",    type: "value", column: "HedgeVolumeLots", op: ">", threshold: 5,  pts: 1.5 },
      { id: "hvol_1",      label: "HedgeVolume > 1 lot",     type: "value", column: "HedgeVolumeLots", op: ">", threshold: 1,  pts: 1 },
      // HedgeDuration group → max 2
      { id: "hdur_300",    label: "AvgHedgeDur < 5 min",     type: "value", column: "AvgHedgeDurationSec", op: "<", threshold: 300,  pts: 2 },
      { id: "hdur_3600",   label: "AvgHedgeDur < 1 hour",    type: "value", column: "AvgHedgeDurationSec", op: "<", threshold: 3600, pts: 1 },
      // Instruments group → max 2
      { id: "instr_metal", label: "Instrument = METALS",     type: "flag", column: "MostTradedInstruments", flagValue: "METALS",  pts: 2 },
      { id: "instr_index", label: "Instrument = INDICES",    type: "flag", column: "MostTradedInstruments", flagValue: "INDICES", pts: 1.5 },
      { id: "instr_enrgy", label: "Instrument = ENERGY",     type: "flag", column: "MostTradedInstruments", flagValue: "ENERGY",  pts: 1 },
      { id: "instr_fx",    label: "Instrument = FX",         type: "flag", column: "MostTradedInstruments", flagValue: "FX",      pts: 0.5 },
    ],
    strategy_groups: [
      { ids: ["ratio_05", "ratio_1"], label: "Loss/Profit Ratio" },
      { ids: ["heavy", "dvol_12", "dvol_5", "dvol_1"], label: "DailyVolumeLots" },
      { ids: ["hft", "freq_80", "freq_50", "freq_25"], label: "TradingFreqPerDay" },
      { ids: ["tsize_05", "tsize_02", "tsize_005"], label: "MostUsedTradeSize" },
      { ids: ["longtrader", "dur_300", "dur_3600"], label: "AvgTradeDuration" },
      { ids: ["long_95", "long_90", "long_80"], label: "Directional Bias" },
      { ids: ["hedger", "hratio_07", "hratio_05", "hratio_03", "ct_hedger"], label: "HedgeRatio" },
      { ids: ["hvol_20", "hvol_5", "hvol_1"], label: "HedgeVolume" },
      { ids: ["hdur_300", "hdur_3600"], label: "HedgeDuration" },
      { ids: ["instr_metal", "instr_index", "instr_enrgy", "instr_fx"], label: "MostTradedInstruments" },
    ],
  }
};

const STORAGE_KEY = "broker_risk_config";

export function getRiskConfig() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return JSON.parse(saved);
  } catch (e) { /* ignore */ }
  return DEFAULT_RISK_CONFIG;
}

export function saveRiskConfig(config) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
}

export function resetRiskConfig() {
  localStorage.removeItem(STORAGE_KEY);
  return DEFAULT_RISK_CONFIG;
}

// ═══ SCORE CALCULATOR ═══
export function calcRiskScore(client, config) {
  const cfg = config || getRiskConfig();
  const safeN = (v) => (v == null || v === "NULL" || v === "" || v === "null") ? 0 : (isNaN(Number(v)) ? 0 : Number(v));

  // Compute derived values
  const eq = safeN(client.Equity), eqPrev = safeN(client.EquityPrevMonth);
  const lossTime = safeN(client.AvgTimeCloseLosingTrades);
  const profTime = safeN(client.AvgTimeCloseProfitableTrades);

  const computed = {
    equityGrowth: (eqPrev > 0 && eq > 0) ? ((eq - eqPrev) / eqPrev) * 100 : -999,
    lossProfitRatio: (lossTime > 0 && profTime > 0) ? lossTime / profTime : 999,
    longPctExtreme: (() => { const lp = safeN(client.LongPct); return lp > 0 ? Math.max(lp, 100 - lp) : -999; })(),
    absSwapInOut: Math.abs(safeN(client.SwapInOut)),
    payoffRatio: (() => { const ap = safeN(client.AvgProfitTrade); const al = Math.abs(safeN(client.AvgLossTrade)); return (ap > 0 && al > 0) ? ap / al : -999; })(),
  };

  function evalFactor(f) {
    if (f.type === "flag") {
      return String(client[f.column]) === f.flagValue ? f.pts : 0;
    }
    let val;
    if (f.type === "computed") {
      val = computed[f.compute];
      if (val === -999 || val === 999) return 0;
    } else {
      val = safeN(client[f.column]);
      // For "less than" comparisons: only apply if client has data (val > 0)
      if ((f.op === "<" || f.op === "<=") && val === 0) return 0;
    }
    if (f.op === ">="  && val >= f.threshold) return f.pts;
    if (f.op === ">"   && val >  f.threshold) return f.pts;
    if (f.op === "<"   && val <  f.threshold) return f.pts;
    if (f.op === "<="  && val <= f.threshold) return f.pts;
    return 0;
  }

  const breakdown = {};
  let total = 0;

  for (const catKey of ["revenue", "position", "compliance", "strategy"]) {
    const factors = cfg.factors[catKey] || [];
    const groups = cfg.factors[catKey + "_groups"] || [];
    const maxPts = cfg.categories[catKey].maxPts;

    const usedIds = new Set();
    let catScore = 0;

    for (const grp of groups) {
      let best = 0;
      let bestId = null;
      for (const fid of grp.ids) {
        const f = factors.find(x => x.id === fid);
        if (!f) continue;
        const sc = evalFactor(f);
        if (sc > best) { best = sc; bestId = fid; }
      }
      if (bestId) usedIds.add(bestId);
      grp.ids.forEach(id => usedIds.add(id));
      catScore += best;
    }

    for (const f of factors) {
      if (usedIds.has(f.id)) continue;
      catScore += evalFactor(f);
    }

    catScore = Math.min(catScore, maxPts);
    breakdown[catKey] = Math.round(catScore * 10) / 10;
    total += catScore;
  }

  return {
    total: Math.min(100, Math.max(0, Math.round(total))),
    breakdown,
  };
}

export function getRiskLevel(score, config) {
  const cfg = config || getRiskConfig();
  for (let i = cfg.levels.length - 1; i >= 0; i--) {
    if (score >= cfg.levels[i].min) return cfg.levels[i];
  }
  return cfg.levels[0];
}

// ═══ CLIENT GROUP SYSTEM ═══

const GROUP_STORAGE_KEY = "client_group_config";

// Condition types available for rules
export const CONDITION_TYPES = {
  minScore:       { label: "Risk Score ≥",         hasValue: true,  unit: "", desc: "Client's total risk score (0-100) is equal to or above this value" },
  maxScore:       { label: "Risk Score <",          hasValue: true,  unit: "", desc: "Client's total risk score (0-100) is below this value" },
  arbitrage:      { label: "Arbitrage Detected",    hasValue: false, desc: "Client shows arbitrage patterns: high ProfitFactor + very fast trades + high win rate + speed flags" },
  minAbuseFlags:  { label: "Abuse Flags ≥",         hasValue: true,  unit: "", desc: "Number of abuse flags (Scalper, HedgingAbuser, Commissioner, NewsTrader, Swaper, HeavyTrader, HFT, IP abuse)" },
  profitable:     { label: "Is Profitable",         hasValue: false, desc: "Client's Revenue risk score is above the 'Profitable' threshold (set in Global Thresholds below)" },
  notProfitable:  { label: "Not Profitable",        hasValue: false, desc: "Client's Revenue risk score is below the 'Profitable' threshold — low revenue risk to the broker" },
  clean:          { label: "Clean Strategy",        hasValue: false, desc: "Client's Compliance risk score is below the 'Clean' threshold — no major policy violations detected" },
  notClean:       { label: "Not Clean",             hasValue: false, desc: "Client has compliance violations — Compliance score is above the 'Clean' threshold" },
  speedFlag:      { label: "Has Speed Flag",        hasValue: false, desc: "Client is flagged as Scalper, HighFrequencyTrader, or NewsTrader (fast execution patterns)" },
  noSpeedFlag:    { label: "No Speed Flag",         hasValue: false, desc: "Client has no speed-related flags — trades at normal pace" },
  default:        { label: "Default (everyone else)", hasValue: false, desc: "Catches all remaining clients not matched by any group above. Put this on your last/lowest priority group" },
};

export const DEFAULT_GROUP_CONFIG = {
  // Priority order — first match wins
  groupOrder: ["B2B_A", "HDG_A", "CRT_A", "SPD_A", "PRO_A", "STD_AB", "STD_B"],
  groups: {
    B2B_A:  {
      name: "Business A-Book", color: "#3b82f6", description: "Business-to-business clients",
      actions: ["Full A-Book execution — Revenue Share LP", "Tags Bridge", "5-Day Swap Free", "Dynamic margin", "NOP limit", "Latency 150ms", "Custom reporting"],
      rules: { matchClientTypes: ["b2b"], conditions: [], logic: "OR" },
    },
    HDG_A:  {
      name: "Hedger A-Book", color: "#eab308", description: "Physical or currency hedgers",
      actions: ["Full A-Book execution", "Swap Free", "High spread & commissions", "Low leverage (1:50)", "NOP (Net Open Position) limit", "Admin fee", "Monitor weekly"],
      rules: { matchClientTypes: ["hedger", "pch_a"], conditions: [], logic: "OR" },
    },
    CRT_A:  {
      name: "Critical Full Restrict", color: "#ef4444", description: "Most dangerous clients, full restriction",
      actions: ["Full A-Book execution", "Latency 150ms", "Slippage enabled", "Daily Swap", "Low leverage (1:50)", "Monitor daily", "Requotes on news events", "NOP limit", "Review for account closure"],
      rules: { matchClientTypes: [], conditions: [{ type: "minScore", value: 49 }, { type: "arbitrage" }, { type: "minAbuseFlags", value: 3 }], logic: "OR" },
    },
    SPD_A:  {
      name: "Speed Risk A-Book", color: "#f97316", description: "Scalper/HFT/NewsTrader + profitable",
      actions: ["Full A-Book execution", "Latency 150ms", "Slippage enabled", "Daily Swap", "Reduced leverage (1:100)", "Monitor daily", "Requotes on news events"],
      rules: { matchClientTypes: [], conditions: [{ type: "speedFlag" }, { type: "profitable" }], logic: "AND" },
    },
    PRO_A:  {
      name: "Professional A-Book", color: "#a855f7", description: "Profitable but clean strategy, respect them",
      actions: ["Full A-Book execution", "Best execution (no delay)", "5-Day Swap Free", "Standard leverage (1:200)", "Monitor monthly", "No promotions"],
      rules: { matchClientTypes: [], conditions: [{ type: "profitable" }, { type: "clean" }, { type: "noSpeedFlag" }], logic: "AND" },
    },
    STD_AB: {
      name: "Standard Hybrid", color: "#06b6d4", description: "Starting to show risk signals",
      actions: ["Hybrid A/B-Book (% based on volume)", "Best execution", "5-Day Swap Free", "Reduced leverage (1:200)", "Monitor weekly", "No promotions"],
      rules: { matchClientTypes: [], conditions: [{ type: "minScore", value: 14 }], logic: "AND" },
    },
    STD_B:  {
      name: "Standard B-Book", color: "#22c55e", description: "Safe clients, broker profit center",
      actions: ["Full B-Book execution", "Best execution (no delay)", "Swap Free", "Standard leverage (1:500)", "Promotions eligible (cashback, loyalty, rebates)"],
      rules: { matchClientTypes: [], conditions: [{ type: "default" }], logic: "AND" },
    },
  },
  thresholds: {
    revenueForProfitable: 10,
    complianceForClean: 10,
    speedFlags: ["Scalper", "HighFrequencyTrader", "NewsTrader"],
    abuseFlags: ["Scalper","HedgingAbuser","Commissioner","NewsTrader","Swaper","HeavyTrader","HighFrequencyTrader"],
  },
  arbitrage: {
    // ALL enabled conditions must be true to flag as arbitrage.
    conditions: [
      { id: "minProfitFactor",   label: "ProfitFactor ≥",          column: "ProfitFactor",       op: ">=", value: 2.0,  enabled: true,  desc: "High profit ratio — arbitrage traders typically above 2.0" },
      { id: "maxAvgDuration",    label: "Avg Trade Duration <",     column: "AvgTradeDuration",   op: "<",  value: 180,  enabled: true,  desc: "Very fast trades (seconds) — 180s = 3 min. Arbitrage needs speed to exploit price gaps" },
      { id: "minProfitPct",      label: "Win Rate (ProfitPct) ≥",   column: "ProfitPct",          op: ">=", value: 65,   enabled: true,  desc: "High win rate — arbitrage traders win most trades. 65% is suspicious combined with speed" },
      { id: "minFastWinRate",    label: "Fast Trade Win Rate ≥",    column: "FastTradeWinRate",   op: ">=", value: 70,   enabled: false, desc: "Win rate on fast trades specifically — high value = exploiting speed advantage" },
      { id: "maxSpeedConsist",   label: "Speed Consistency <",      column: "SpeedConsistency",   op: "<",  value: 0.25, enabled: false, desc: "Low speed consistency = robotic/automated pattern. Human traders vary more" },
      { id: "minPayoffRatio",    label: "Payoff Ratio ≥",           column: "PayoffRatio",        op: ">=", value: 3,    enabled: false, desc: "Avg win / avg loss — high ratio with fast trades = classic arbitrage signature", compute: "payoffRatio" },
      { id: "minTradingFreq",    label: "Trading Frequency/Day ≥",  column: "TradingFreqPerDay",  op: ">=", value: 30,   enabled: false, desc: "High trades per day — automated or systematic pattern" },
      { id: "minDailyVolume",    label: "Daily Volume (lots) ≥",    column: "DailyVolumeLots",    op: ">=", value: 3,    enabled: false, desc: "High daily volume — pushing significant volume quickly" },
      { id: "maxTradeSize",      label: "Most Used Trade Size <",   column: "MostUsedTradeSize",  op: "<",  value: 0.5,  enabled: false, desc: "Small individual trades — many small positions instead of big ones" },
      { id: "flagScalper",       label: "Is Scalper",               column: "Scalper",            op: "flag", value: "Scalper",       enabled: true,  desc: "Scalper flag — fast entry/exit pattern detected by system" },
      { id: "flagGainer",        label: "Is GainerTrader",          column: "GainerTrader",       op: "flag", value: "GainerTrader", enabled: false, desc: "GainerTrader flag — consistent profit extraction. Not all arb traders are Gainers" },
      { id: "flagHFT",           label: "Is HighFrequencyTrader",   column: "HighFrequencyTrader",op: "flag", value: "HighFrequencyTrader", enabled: false, desc: "HFT flag — extremely high trade count" },
      { id: "flagNewsTrader",    label: "Is NewsTrader",            column: "NewsTrader",         op: "flag", value: "NewsTrader",    enabled: false, desc: "Trades around news events — potential latency arbitrage on news spikes" },
    ],
  },
};

export function getGroupConfig() {
  try {
    const saved = localStorage.getItem(GROUP_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Migration: old config without groupOrder/rules → add defaults
      if (!parsed.groupOrder) {
        return migrateOldConfig(parsed);
      }
      // Migration: old arbitrage format (minProfitFactor/requiredFlags) → new conditions format
      if (parsed.arbitrage && !parsed.arbitrage.conditions) {
        return migrateOldConfig(parsed);
      }
      return parsed;
    }
  } catch (e) { /* ignore */ }
  return DEFAULT_GROUP_CONFIG;
}

function migrateOldConfig(old) {
  const def = DEFAULT_GROUP_CONFIG;
  const newCfg = { ...def };
  if (old.groups) {
    Object.keys(old.groups).forEach(gk => {
      if (newCfg.groups[gk]) {
        newCfg.groups[gk] = { ...newCfg.groups[gk], name: old.groups[gk].name, color: old.groups[gk].color, description: old.groups[gk].description, actions: old.groups[gk].actions };
      }
    });
  }
  if (old.thresholds) newCfg.thresholds = { ...def.thresholds, ...old.thresholds };
  if (old.arbitrage && !old.arbitrage.conditions) {
    const arbConds = [...def.arbitrage.conditions];
    if (old.arbitrage.minProfitFactor != null) {
      const c = arbConds.find(x => x.id === "minProfitFactor");
      if (c) c.value = old.arbitrage.minProfitFactor;
    }
    if (old.arbitrage.maxAvgDuration != null) {
      const c = arbConds.find(x => x.id === "maxAvgDuration");
      if (c) c.value = old.arbitrage.maxAvgDuration;
    }
    newCfg.arbitrage = { conditions: arbConds };
  } else if (old.arbitrage?.conditions) {
    newCfg.arbitrage = old.arbitrage;
  }
  saveGroupConfig(newCfg);
  return newCfg;
}

export function saveGroupConfig(config) {
  localStorage.setItem(GROUP_STORAGE_KEY, JSON.stringify(config));
}

export function resetGroupConfig() {
  localStorage.removeItem(GROUP_STORAGE_KEY);
  return DEFAULT_GROUP_CONFIG;
}

// ═══ RULE-DRIVEN GROUP ASSIGNMENT ═══

function evalCondition(cond, ctx) {
  switch (cond.type) {
    case "minScore":       return ctx.score >= (cond.value || 0);
    case "maxScore":       return ctx.score < (cond.value || 0);
    case "arbitrage":      return ctx.isArbitrage;
    case "minAbuseFlags":  return ctx.activeAbuseCount >= (cond.value || 0);
    case "profitable":     return ctx.isProfitable;
    case "notProfitable":  return !ctx.isProfitable;
    case "clean":          return ctx.isClean;
    case "notClean":       return !ctx.isClean;
    case "speedFlag":      return ctx.hasSpeedFlag;
    case "noSpeedFlag":    return !ctx.hasSpeedFlag;
    case "default":        return true;
    default:               return false;
  }
}

export function calcClientGroup(client, riskResult, groupConfig) {
  const gcfg = groupConfig || getGroupConfig();
  const th = gcfg.thresholds;
  const arb = gcfg.arbitrage;
  const safeN = (v) => (v == null || v === "NULL" || v === "" || v === "null") ? 0 : (isNaN(Number(v)) ? 0 : Number(v));

  const score = riskResult.total;
  const bd = riskResult.breakdown;
  const clientType = String(client.ClientType || "").toLowerCase();

  // Count flags
  const abuseFlags = th.abuseFlags || ["Scalper","HedgingAbuser","Commissioner","NewsTrader","Swaper","HeavyTrader","HighFrequencyTrader"];
  const allFlags = [...new Set([...abuseFlags, "Hedger","LongTrader","GainerTrader"])];
  const activeFlags = allFlags.filter(f => client[f] === f);
  const hasIPAbuse = !!(client.IP_ODT || client.Login_Same_IP || client.CU_Same_IP);
  const activeAbuseCount = abuseFlags.filter(f => client[f] === f).length + (hasIPAbuse ? 1 : 0);

  // Detect arbitrage — ALL enabled conditions must be true
  const arbConds = (arb.conditions || []).filter(c => c.enabled);
  let isArbitrage = arbConds.length > 0;
  for (const c of arbConds) {
    // Support computed fields in arbitrage conditions
    let val;
    if (c.compute === "payoffRatio") {
      const ap = safeN(client.AvgProfitTrade);
      const al = Math.abs(safeN(client.AvgLossTrade));
      val = (ap > 0 && al > 0) ? ap / al : 0;
    } else {
      val = safeN(client[c.column]);
    }

    if (c.op === "flag") {
      if (String(client[c.column]) !== c.value) { isArbitrage = false; break; }
    } else if (c.op === ">=") {
      if (val < c.value) { isArbitrage = false; break; }
    } else if (c.op === "<") {
      if (val === 0 || val >= c.value) { isArbitrage = false; break; }
    } else if (c.op === "<=") {
      if (val > c.value) { isArbitrage = false; break; }
    } else if (c.op === ">") {
      if (val <= c.value) { isArbitrage = false; break; }
    }
  }

  // Evaluate context
  const hasSpeedFlag = (th.speedFlags || []).some(f => client[f] === f);
  const isProfitable = bd.revenue >= (th.revenueForProfitable || 10);
  const isClean = bd.compliance < (th.complianceForClean || 10);

  const ctx = { score, bd, clientType, activeFlags, activeAbuseCount, isArbitrage, hasSpeedFlag, isProfitable, isClean };

  // ── Rule-driven priority assignment ──
  let group = gcfg.groupOrder[gcfg.groupOrder.length - 1] || "STD_B";

  for (const gk of gcfg.groupOrder) {
    const g = gcfg.groups[gk];
    if (!g || !g.rules) continue;

    const types = g.rules.matchClientTypes || [];
    if (types.length > 0) {
      if (types.some(t => clientType === t.toLowerCase().trim())) {
        group = gk; break;
      }
      continue;
    }

    const conds = g.rules.conditions || [];
    if (conds.length === 0) continue;

    const results = conds.map(c => evalCondition(c, ctx));
    const match = g.rules.logic === "OR" ? results.some(Boolean) : results.every(Boolean);
    if (match) { group = gk; break; }
  }

  return {
    group,
    isArbitrage,
    activeFlags,
    activeAbuseCount,
    isProfitable,
    isClean,
    hasSpeedFlag,
    score,
    breakdown: bd,
  };
}
